import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Card, EmptyState, PageHeader } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  CalendarDays, List as ListIcon, Plus, Clock, MapPin, Video, Phone, Zap,
  ChevronLeft, ChevronRight, Check, CheckCheck, X, Trash2, Copy, ExternalLink, Ticket,
} from "lucide-react";
import { AppointmentDetailDialog } from "@/components/AppointmentDetailDialog";
import { FreeCodesDialog } from "@/components/FreeCodesDialog";

const MEETING_ICON = { "In Person": MapPin, Phone: Phone, Video: Video };
const STATUSES = ["Requested", "Confirmed", "Completed", "Cancelled"];

const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayISO = () => fmtISO(new Date());
function startOfWeek(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
const dayLabel = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

export default function Appointments() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || (user?.permissions || []).includes("manage_appointments");
  const [sp] = useSearchParams();
  const jumpDate = /^\d{4}-\d{2}-\d{2}$/.test(sp.get("date") || "") ? sp.get("date") : null;
  const [mode, setMode] = useState(jumpDate ? "today" : "week"); // today | week | month
  const [view, setView] = useState("calendar"); // calendar | list
  const [cursor, setCursor] = useState(() => (jumpDate ? new Date(`${jumpDate}T12:00:00`) : new Date()));
  useEffect(() => { if (jumpDate) { setMode("today"); setCursor(new Date(`${jumpDate}T12:00:00`)); } }, [jumpDate]);
  const [appts, setAppts] = useState(null);
  const [gEvents, setGEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [settings, setSettings] = useState(null);
  const [fStaff, setFStaff] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fType, setFType] = useState("");
  const [openId, setOpenId] = useState(null);
  const [codesOpen, setCodesOpen] = useState(false);

  const range = useMemo(() => {
    if (mode === "today") return { frm: cursor, to: cursor, days: [new Date(cursor)] };
    if (mode === "month") {
      const gridStart = startOfWeek(startOfMonth(cursor));
      const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
      return { frm: gridStart, to: days[41], days };
    }
    const s = startOfWeek(cursor);
    const days = Array.from({ length: 7 }, (_, i) => addDays(s, i));
    return { frm: s, to: days[6], days };
  }, [mode, cursor]);

  const frm = fmtISO(range.frm), to = fmtISO(range.to);
  const load = () => {
    const qs = new URLSearchParams({ frm, to });
    if (fStaff) qs.set("staff_id", fStaff);
    if (fStatus) qs.set("status", fStatus);
    if (fType) qs.set("type", fType);
    api.get(`/appointments?${qs.toString()}`).then((r) => setAppts(r.data)).catch(() => setAppts([]));
    api.get(`/google-calendar/events?start=${frm}&end=${to}`).then((r) => setGEvents(r.data.map((e) => ({ ...e, google: true })))).catch(() => setGEvents([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [frm, to, fStaff, fStatus, fType]);
  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const types = settings?.appointment_types || [];
  const shift = (dir) => setCursor((c) =>
    mode === "today" ? addDays(c, dir) : mode === "month" ? new Date(c.getFullYear(), c.getMonth() + dir, 1) : addDays(c, dir * 7));
  const setStatus = async (a, status) => {
    try { await api.put(`/appointments/${a.id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (a) => {
    if (!window.confirm("Delete this appointment?")) return;
    try { await api.delete(`/appointments/${a.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const byDay = (iso) => [...(appts || []), ...gEvents].filter((a) => a.date === iso).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const rangeLabel = mode === "today" ? dayLabel(cursor)
    : mode === "month" ? cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : `${dayLabel(range.days[0])} – ${dayLabel(range.days[6])}`;

  return (
    <div>
      <PageHeader title="Appointments" subtitle="All appointments in one place — calendar & list views, double-booking protection and emergency slots.">
        <div className="flex flex-wrap items-center gap-2">
          {canManage && (() => {
            const bookingUrl = `${window.location.origin}/book-appointment`;
            const copyLink = async () => {
              try { await navigator.clipboard.writeText(bookingUrl); toast.success("Public booking link copied"); }
              catch { toast.error("Couldn't copy — link: " + bookingUrl); }
            };
            return (
              <>
                <button data-testid="copy-booking-link" onClick={copyLink} title={bookingUrl} className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-700 px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-slate-50"><Copy className="h-4 w-4" /> Copy booking link</button>
                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="open-booking-link" title="Open public booking page" className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-700 px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-slate-50"><ExternalLink className="h-4 w-4" /> Open</a>
              </>
            );
          })()}
          <button data-testid="free-codes-btn" onClick={() => setCodesOpen(true)} className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-700 px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-slate-50"><Ticket className="h-4 w-4" /> Free codes</button>
          {canManage && <Link to="/admin/book" data-testid="book-appointment-link" className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm"><Plus className="h-4 w-4" /> Book Appointment</Link>}
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5" data-testid="range-toggle">
          {["today", "week", "month"].map((m) => (
            <button key={m} data-testid={`range-${m}`} onClick={() => setMode(m)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold capitalize ${mode === m ? "bg-primary text-white" : "text-slate-600"}`}>{m}</button>
          ))}
        </div>
        <button data-testid="range-prev" onClick={() => shift(-1)} className="p-2 rounded-lg border border-slate-200 bg-white"><ChevronLeft className="h-4 w-4" /></button>
        <button data-testid="range-current" onClick={() => setCursor(new Date())} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold">Today</button>
        <button data-testid="range-next" onClick={() => shift(1)} className="p-2 rounded-lg border border-slate-200 bg-white"><ChevronRight className="h-4 w-4" /></button>
        <div className="text-sm font-semibold text-slate-700 ml-1">{rangeLabel}</div>
        <div className="ml-auto inline-flex rounded-xl border border-slate-200 bg-white p-0.5" data-testid="view-toggle">
          <button data-testid="view-calendar" onClick={() => setView("calendar")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1 ${view === "calendar" ? "bg-secondary text-white" : "text-slate-600"}`}><CalendarDays className="h-4 w-4" /> Calendar</button>
          <button data-testid="view-list" onClick={() => setView("list")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1 ${view === "list" ? "bg-secondary text-white" : "text-slate-600"}`}><ListIcon className="h-4 w-4" /> List</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select data-testid="filter-staff" value={fStaff} onChange={(e) => setFStaff(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"><option value="">All staff / consultants</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        <select data-testid="filter-type" value={fType} onChange={(e) => setFType(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"><option value="">All types</option>{types.map((t) => <option key={t}>{t}</option>)}</select>
      </div>

      {!appts ? <Loader /> : view === "list"
        ? <ApptList appts={appts} canManage={canManage} setStatus={setStatus} remove={remove} onOpen={setOpenId} />
        : <ApptCalendar mode={mode} days={range.days} cursor={cursor} byDay={byDay} canManage={canManage} setStatus={setStatus} remove={remove} onOpen={setOpenId} />}
      {openId && <AppointmentDetailDialog id={openId} canManage={canManage} onClose={() => setOpenId(null)} onChanged={load} />}
      {codesOpen && <FreeCodesDialog onClose={() => setCodesOpen(false)} />}
    </div>
  );
}

function ApptActions({ a, canManage, setStatus, remove, prefix }) {
  if (!canManage) return null;
  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {a.status !== "Confirmed" && a.status !== "Completed" && <button title="Confirm" data-testid={`${prefix}-confirm-${a.id}`} onClick={() => setStatus(a, "Confirmed")} className="p-1 rounded hover:bg-emerald-50 text-emerald-600"><Check className="h-3.5 w-3.5" /></button>}
      {a.status !== "Completed" && <button title="Complete" data-testid={`${prefix}-complete-${a.id}`} onClick={() => setStatus(a, "Completed")} className="p-1 rounded hover:bg-sky-50 text-sky-600"><CheckCheck className="h-3.5 w-3.5" /></button>}
      {a.status !== "Cancelled" && <button title="Cancel" data-testid={`${prefix}-cancel-${a.id}`} onClick={() => setStatus(a, "Cancelled")} className="p-1 rounded hover:bg-amber-50 text-amber-600"><X className="h-3.5 w-3.5" /></button>}
      <button title="Delete" data-testid={`${prefix}-delete-${a.id}`} onClick={() => remove(a)} className="p-1 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function ContactName({ a }) {
  const inner = <>{a.contact_name || a.client_name}{a.contact_type === "lead" && <span className="text-[9px] font-bold uppercase bg-violet-100 text-violet-700 px-1 rounded ml-1">Lead</span>}</>;
  if (a.client_id) return <Link to={`/admin/clients/${a.client_id}`} onClick={(e) => e.stopPropagation()} className="hover:text-primary">{inner}</Link>;
  return <span>{inner}</span>;
}

function EtransferTag({ a }) {
  const [status, setStatus] = useState(a.payment_status);
  if (a.payment_method !== "e-Transfer") return null;
  const view = async () => {
    try {
      const r = await api.get(`/appointments/${a.id}/receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const verify = async () => {
    try { await api.post(`/appointments/${a.id}/payment-received`); a.payment_status = "Payment received"; setStatus("Payment received"); toast.success("e-Transfer marked as received"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const ok = status === "Payment received";
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`appt-etransfer-${a.id}`} onClick={(e) => e.stopPropagation()}>
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
        e-Transfer ${a.amount_due} · {ok ? "Received" : "Check receipt"}
      </span>
      <button onClick={view} data-testid={`appt-receipt-view-${a.id}`} className="text-[10px] font-semibold text-primary hover:underline">View receipt</button>
      {!ok && <button onClick={verify} data-testid={`appt-receipt-verify-${a.id}`} className="text-[10px] font-semibold text-emerald-700 hover:underline">Mark received</button>}
    </div>
  );
}

function GoogleItem({ e }) {
  return (
    <Card data-testid={`gcal-event-${e.id}`} className="p-3 bg-slate-50 border-dashed">
      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600"><Clock className="h-3 w-3 text-slate-400" />{e.all_day ? "All day" : `${e.start_time}${e.end_time ? `–${e.end_time}` : ""}`}</div>
      <div className="text-sm font-semibold text-slate-700 mt-1 truncate">{e.summary}</div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mt-1">Google Calendar{e.busy ? " · busy" : ""}</div>
    </Card>
  );
}

function MeetLink({ a }) {
  if (!a.meet_link) return null;
  return <a href={a.meet_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`appt-meet-${a.id}`} className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-primary hover:underline"><Video className="h-3 w-3" /> Join Google Meet</a>;
}

function ApptCard({ a, canManage, setStatus, remove, onOpen }) {
  const MIcon = MEETING_ICON[a.meeting_type] || Video;
  return (
    <Card data-testid={`appt-${a.id}`} onClick={() => onOpen(a.id)} title="Open details" className={`p-3 cursor-pointer hover:shadow-md hover:border-primary/40 transition-shadow ${a.is_emergency ? "border-primary/40 bg-primary/5" : ""} ${a.status === "Cancelled" ? "opacity-50" : ""}`}>
      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800"><Clock className="h-3 w-3 text-slate-400" />{a.start_time}{a.end_time ? `–${a.end_time}` : ""}{a.is_emergency && <Zap className="h-3 w-3 text-primary" />}</div>
      <div className="text-sm font-semibold text-slate-900 mt-1 truncate"><ContactName a={a} /></div>
      <div className="text-[11px] text-slate-500 truncate">{a.type}</div>
      <div className="flex items-center gap-1 mt-1 text-[11px] text-slate-500"><MIcon className="h-3 w-3" /> {a.meeting_type}{a.staff_name ? ` · ${a.staff_name}` : ""}</div>
      <EtransferTag a={a} />
      <MeetLink a={a} />
      <div className="mt-2 flex items-center justify-between gap-1">
        <StatusBadge status={a.status} />
        <ApptActions a={a} canManage={canManage} setStatus={setStatus} remove={remove} prefix="appt" />
      </div>
    </Card>
  );
}

function ApptCalendar({ mode, days, cursor, byDay, canManage, setStatus, remove, onOpen }) {
  const today = todayISO();
  if (mode === "today") {
    const items = byDay(fmtISO(days[0]));
    return (
      <div className="space-y-2 max-w-2xl" data-testid="appt-calendar-today">
        {items.length === 0 ? <Card className="p-6"><EmptyState icon={CalendarDays} title="No appointments" description="Nothing scheduled for this day." /></Card>
          : items.map((a) => a.google ? <GoogleItem key={a.id} e={a} /> : <ApptCard key={a.id} a={a} canManage={canManage} setStatus={setStatus} remove={remove} onOpen={onOpen} />)}
      </div>
    );
  }
  if (mode === "month") {
    const monthIdx = cursor.getMonth();
    return (
      <div data-testid="appt-calendar-month">
        <div className="grid grid-cols-7 gap-2 mb-1 text-[11px] font-bold text-slate-400">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-1">{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-2">
          {days.map((day) => {
            const iso = fmtISO(day); const items = byDay(iso); const inMonth = day.getMonth() === monthIdx; const isToday = iso === today;
            return (
              <div key={iso} className={`min-h-[6rem] rounded-lg border p-1.5 ${inMonth ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100"} ${isToday ? "ring-2 ring-primary/40" : ""}`}>
                <div className={`text-[11px] font-bold mb-1 ${isToday ? "text-primary" : inMonth ? "text-slate-600" : "text-slate-300"}`}>{day.getDate()}</div>
                <div className="space-y-1">
                  {items.slice(0, 3).map((a) => a.google ? (
                    <div key={a.id} data-testid={`gcal-mini-${a.id}`} title="Google Calendar" className="text-[10px] px-1.5 py-0.5 rounded truncate bg-slate-100 text-slate-500 border border-dashed border-slate-300">{a.all_day ? "All day" : a.start_time} {a.summary}</div>
                  ) : (
                    <div key={a.id} data-testid={`appt-mini-${a.id}`} onClick={() => onOpen(a.id)} className={`cursor-pointer text-[10px] px-1.5 py-0.5 rounded truncate ${a.status === "Cancelled" ? "bg-slate-100 text-slate-400 line-through" : a.is_emergency ? "bg-primary/10 text-primary" : "bg-secondary/10 text-secondary"}`}>{a.start_time} {a.contact_name || a.client_name}</div>
                  ))}
                  {items.length > 3 && <div className="text-[10px] text-slate-400 px-1">+{items.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <div className="grid gap-3 md:grid-cols-7" data-testid="appt-calendar-week">
      {days.map((day) => {
        const iso = fmtISO(day); const items = byDay(iso); const isToday = iso === today;
        return (
          <div key={iso} className="min-h-[8rem]">
            <div className={`text-xs font-bold mb-2 px-1 ${isToday ? "text-primary" : "text-slate-500"}`}>{dayLabel(day)}</div>
            <div className="space-y-2">
              {items.length === 0 && <div className="text-[11px] text-slate-300 px-1">—</div>}
              {items.map((a) => a.google ? <GoogleItem key={a.id} e={a} /> : <ApptCard key={a.id} a={a} canManage={canManage} setStatus={setStatus} remove={remove} onOpen={onOpen} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ApptList({ appts, canManage, setStatus, remove, onOpen }) {
  const sorted = [...appts].sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
  if (sorted.length === 0) return <Card className="p-6"><EmptyState icon={CalendarDays} title="No appointments" description="No appointments match these filters." /></Card>;
  return (
    <Card className="p-0 overflow-hidden" data-testid="appt-list">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>{["Name", "Date", "Time", "Staff", "Type", "Meeting", "Status", ""].map((h, i) => <th key={i} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((a) => {
              const MIcon = MEETING_ICON[a.meeting_type] || Video;
              return (
                <tr key={a.id} data-testid={`appt-list-row-${a.id}`} onClick={() => onOpen(a.id)} className={`cursor-pointer hover:bg-slate-50 ${a.status === "Cancelled" ? "opacity-60" : ""}`}>
                  <td className="px-4 py-3"><div className="flex items-center gap-1.5 font-semibold text-slate-800"><ContactName a={a} />{a.is_emergency && <Zap className="h-3 w-3 text-primary" />}</div></td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{a.date}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{a.start_time}{a.end_time ? `–${a.end_time}` : ""}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{a.staff_name || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{a.type}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600"><span className="inline-flex items-center gap-1"><MIcon className="h-3.5 w-3.5" />{a.meeting_type}</span><div><MeetLink a={a} /></div></td>
                  <td className="px-4 py-3"><StatusBadge status={a.status} /><EtransferTag a={a} /></td>
                  <td className="px-4 py-3 whitespace-nowrap text-right"><div className="flex justify-end"><ApptActions a={a} canManage={canManage} setStatus={setStatus} remove={remove} prefix="appt-list" /></div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
